import React, { useCallback, useEffect, useState } from "react";
import { Loader2, PlayCircle, FlaskConical, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { formatDateTime } from "@/lib/datetime";
import { Card, errMsg } from "@/components/email/EmParts";
import BrandForm from "@/components/email/BrandForm";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

function FooterForm({ s, manage, onSaved }) {
  const [addr, setAddr] = useState(s.postal_address || "");
  const [name, setName] = useState(s.footer_company_name || "");
  const save = async () => { try { await api.put("/email-marketing/settings/footer", { postal_address: addr, company_name: name }); toast.success("Footer saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Card title="Marketing email footer" testId="em-settings-footer">
      <p className="text-xs text-slate-500 mb-2">Every marketing email includes your business name, postal address and an unsubscribe link (UK PECR).</p>
      <div className="space-y-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Business name shown in footer" disabled={!manage} data-testid="em-footer-company" />
        <Textarea value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="Business postal address" disabled={!manage} data-testid="em-footer-address" />
        {manage && <Button variant="outline" onClick={save} data-testid="em-footer-save">Save footer</Button>}
      </div>
    </Card>
  );
}

const LABELS = { customers: "Customer records", excluded_staff: "Staff excluded", already_linked: "Already linked", to_create: "New subscribers", to_adopt: "Standalone adopted", missing_email: "No email", invalid_email: "Invalid email", errors: "Errors" };

function RunResult({ run }) {
  const v = run.verification;
  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`em-run-${run.id}`}>
      <p className="text-sm font-semibold text-slate-800">{run.dry_run ? "Dry-run (nothing written)" : "Backfill run"} · <span className="font-normal text-slate-500">{formatDateTime(run.finished_at)} · {run.actor}</span></p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {Object.entries(LABELS).map(([k, l]) => <div key={k} className="text-xs"><span className="text-slate-400">{l}</span><p className="font-semibold tabular-nums text-slate-800" data-testid={`em-run-${k}`}>{(run.counts?.[k] || 0).toLocaleString()}</p></div>)}
      </div>
      {v && <p className={`text-xs ${v.ok ? "text-emerald-700" : "text-amber-700"}`} data-testid="em-run-verification">{v.ok ? `Verified: ${v.customers.toLocaleString()} customers, ${v.linked_subscribers.toLocaleString()} linked subscribers, no gaps or duplicates` : `${v.customers_without_subscriber} customers without subscriber, ${v.orphan_subscribers} orphans, ${v.duplicate_links} duplicates`}</p>}
      {run.errors?.length > 0 && <ul className="text-xs text-rose-700 list-disc pl-4">{run.errors.slice(0, 5).map((e, i) => <li key={i}>{e.error}</li>)}</ul>}
    </div>
  );
}

export default function EmSettings() {
  const { can } = useAuth();
  const manage = can("email_marketing.manage");
  const [s, setS] = useState(null);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => {
    api.get("/email-marketing/settings").then((r) => setS(r.data)).catch((e) => toast.error(errMsg(e)));
    if (manage) api.get("/email-marketing/backfill/runs").then((r) => setRuns(r.data)).catch(() => {});
  }, [manage]);
  useEffect(() => { load(); }, [load]);
  const toggle = async (v) => { try { const { data } = await api.put("/email-marketing/settings", { sync_enabled: v }); setS(data); toast.success(v ? "Customer sync enabled" : "Customer sync disabled"); } catch (e) { toast.error(errMsg(e)); } };
  const backfill = async (dry) => {
    if (!dry && !window.confirm("Create subscriber records for all customers in this company? Everyone starts with marketing permission 'Unknown'. No emails are sent.")) return;
    setBusy(dry ? "dry" : "run");
    try {
      await api.post("/email-marketing/backfill", null, { params: { dry_run: dry } });
      if (dry) { toast.success("Dry-run complete"); load(); setBusy(""); return; }
      toast.info("Backfill started. This can take a minute.");
      const poll = setInterval(async () => {
        const { data } = await api.get("/email-marketing/backfill/status").catch(() => ({ data: { running: false } }));
        if (!data.running) { clearInterval(poll); setBusy(""); toast.success("Backfill complete"); load(); }
      }, 3000);
    } catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };
  if (!s) return <Loader2 className="w-5 h-5 animate-spin text-slate-400" />;
  return (
    <div className="space-y-4 max-w-4xl" data-testid="em-settings">
      <Card title="Customer synchronisation" testId="em-settings-sync">
        <div className="flex items-start justify-between gap-4">
          <div className="text-sm text-slate-600 space-y-1">
            <p>Every customer gets a linked subscriber record. Name, email, phone and location changes sync automatically; unsubscribes and suppressions are never reset.</p>
            <p className="text-xs text-slate-400">New customers start as "No consent" until a consent source is recorded. Staff records are excluded.{s.last_sweep_at ? ` Last background check ${formatDateTime(s.last_sweep_at)}.` : ""}</p>
          </div>
          <Switch checked={!!s.sync_enabled} disabled={!manage} onCheckedChange={toggle} data-testid="em-sync-toggle" />
        </div>
      </Card>
      {manage && (
        <Card title="Existing customer backfill" testId="em-settings-backfill" right={<ShieldCheck className="w-4 h-4 text-emerald-600" />}>
          <p className="text-sm text-slate-600 mb-3">Safe to repeat: existing links are updated, never duplicated. Run a dry-run first to see the counts. No emails are sent.</p>
          <div className="flex flex-wrap gap-2 mb-4">
            <Button variant="outline" className="gap-1.5" disabled={!!busy || !s.sync_enabled} onClick={() => backfill(true)} data-testid="em-backfill-dry-btn">{busy === "dry" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}Dry-run</Button>
            <Button className="gap-1.5" disabled={!!busy || !s.sync_enabled} onClick={() => backfill(false)} data-testid="em-backfill-run-btn">{busy === "run" ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}Run backfill</Button>
          </div>
          <div className="space-y-3">{runs.map((r) => <RunResult key={r.id} run={r} />)}{!runs.length && <p className="text-sm text-slate-400" data-testid="em-runs-empty">No runs yet.</p>}</div>
        </Card>)}
      <FooterForm s={s} manage={manage} onSaved={load} />
      <BrandForm manage={manage} />
      <Card title="Sending" testId="em-settings-sending"><p className="text-sm text-slate-500">SendGrid API key, sender, domain authentication, unsubscribe group and signed webhook are managed in <Link to="/settings/integrations" className="text-indigo-600 hover:underline" data-testid="em-settings-sendgrid-link">Integrations → Twilio SendGrid</Link>. Live campaign sending stays locked until approved.</p></Card>
    </div>
  );
}
